import React, { useState, useEffect, useMemo } from 'react';
import { SkipForward, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import PlayerSettings from './PlayerSettings';
import DeckControls from './DeckControls';
import DeckDropZone from './DeckDropZone';

const DualDeckPlayer = ({ 
  decksInfo, 
  currentTrack, 
  nextTrack,
  deckATrack,
  deckBTrack,
  onPlayPause, 
  onNext, 
  onVolumeChange,
  onDeckVolumeChange,
  onCrossfaderChange,
  onSettingsChange,
  onPitchChange,
  onSeek,
  onCue,
  onLoop,
  onSync,
  onDeckDrop,
  playerSettings,
  volume,
  playbackInfo,
  pitchInfoA = null,  // Smart Mix v2
  pitchInfoB = null,   // Smart Mix v2
  currentMode = null,  // Mode de la partie actuelle
  loopSizeA = null,    // Loop size for deck A
  loopSizeB = null,    // Loop size for deck B
  onLoopChange = null, // Callback when loop changes
  loopRollSizeA = null, // Loop-roll size for deck A
  loopRollSizeB = null, // Loop-roll size for deck B
  onLoopRollStart = null, // Callback when loop-roll starts
  onLoopRollEnd = null, // Callback when loop-roll ends
}) => {
  const [pitchA, setPitchA] = useState(0);
  const [pitchB, setPitchB] = useState(0);
  const [volumeA, setVolumeA] = useState(100);
  const [volumeB, setVolumeB] = useState(100);

  const activeDeck = decksInfo?.activeDeck || 'A';
  const deckAInfo = decksInfo?.deckA;
  const deckBInfo = decksInfo?.deckB;
  const crossfaderPosition = decksInfo?.crossfaderPosition || 0;
  
  // ═══════════════════════════════════════════════════════════════
  // CRITICAL FIX: Sync volumes with actual audio volume from audioPlayer
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (deckAInfo && deckAInfo.volume !== undefined) {
      const volumePercent = Math.round(deckAInfo.volume * 100);
      setVolumeA(volumePercent);
    }
  }, [deckAInfo?.volume]);
  
  useEffect(() => {
    if (deckBInfo && deckBInfo.volume !== undefined) {
      const volumePercent = Math.round(deckBInfo.volume * 100);
      setVolumeB(volumePercent);
    }
  }, [deckBInfo?.volume]);

  const handlePitchChange = (deck, value) => {
    if (deck === 'A') {
      setPitchA(value);
    } else {
      setPitchB(value);
    }
    onPitchChange && onPitchChange(deck, value);
  };

  const handleDeckVolumeChange = (deck, value) => {
    if (deck === 'A') {
      setVolumeA(value);
    } else {
      setVolumeB(value);
    }
    onDeckVolumeChange && onDeckVolumeChange(deck, value);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Dual Decks with Professional Controls - Responsive Grid */}
      <div 
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6"
        role="region"
        aria-label="Platines DJ"
      >
        {/* Deck A with Professional Controls & Drop Zone */}
        <div 
          className="relative"
          role="article"
          aria-label="Platine A"
        >
          <DeckControls
            deckId="A"
            track={deckATrack || (activeDeck === 'A' ? currentTrack : nextTrack)}
            isPlaying={deckAInfo ? !deckAInfo.paused : false}
            currentTime={deckAInfo?.currentTime || 0}
            duration={deckAInfo?.duration || 0}
            pitch={pitchA}
            volume={volumeA}
            onPlayPause={() => onPlayPause('A')}
            onPitchChange={(value) => handlePitchChange('A', value)}
            onVolumeChange={(value) => handleDeckVolumeChange('A', value)}
            onSeek={(position) => onSeek('A', position)}
            onCue={(position) => onCue('A', position)}
            onLoop={onLoop}
            onSync={() => onSync('A')}
            isActive={activeDeck === 'A'}
            pitchInfo={pitchInfoA}
            currentMode={currentMode}
            loopSize={loopSizeA}
            onLoopChange={onLoopChange}
            loopRollSize={loopRollSizeA}
            onLoopRollStart={onLoopRollStart}
            onLoopRollEnd={onLoopRollEnd}
          />
          <DeckDropZone
            deckId="A"
            isActive={activeDeck === 'A'}
            onDrop={onDeckDrop}
            disabled={deckAInfo && !deckAInfo.paused}
          />
        </div>

        {/* Deck B with Professional Controls & Drop Zone */}
        <div 
          className="relative"
          role="article"
          aria-label="Platine B"
        >
          <DeckControls
            deckId="B"
            track={deckBTrack || (activeDeck === 'B' ? currentTrack : nextTrack)}
            isPlaying={deckBInfo ? !deckBInfo.paused : false}
            currentTime={deckBInfo?.currentTime || 0}
            duration={deckBInfo?.duration || 0}
            pitch={pitchB}
            volume={volumeB}
            onPlayPause={() => onPlayPause('B')}
            onPitchChange={(value) => handlePitchChange('B', value)}
            onVolumeChange={(value) => handleDeckVolumeChange('B', value)}
            onSeek={(position) => onSeek('B', position)}
            onCue={(position) => onCue('B', position)}
            onLoop={onLoop}
            onSync={() => onSync('B')}
            isActive={activeDeck === 'B'}
            pitchInfo={pitchInfoB}
            currentMode={currentMode}
            loopSize={loopSizeB}
            onLoopChange={onLoopChange}
            loopRollSize={loopRollSizeB}
            onLoopRollStart={onLoopRollStart}
            onLoopRollEnd={onLoopRollEnd}
          />
          <DeckDropZone
            deckId="B"
            isActive={activeDeck === 'B'}
            onDrop={onDeckDrop}
            disabled={deckBInfo && !deckBInfo.paused}
          />
        </div>
      </div>

      {/* Crossfader + Controls - ALIGNED ON SAME LINE */}
      <div className="bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-2xl p-6">
        {/* Settings Button */}
        <div className="flex justify-end mb-4">
          <PlayerSettings 
            settings={playerSettings}
            onSettingsChange={onSettingsChange}
          />
        </div>

        {/* Crossfader + Next Button on same line */}
        <div className="flex items-center gap-6 mb-6">
          {/* Crossfader - Left side */}
          <div className="flex-1" role="region" aria-label="Crossfader - Contrôle de mixage entre platines">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-blue-400" aria-label="Volume Platine A">
                  DECK A
                </span>
                <span className="text-xs text-sunset-orange/60">CROSSFADER</span>
                <span className="text-xs font-semibold text-purple-400" aria-label="Volume Platine B">
                  DECK B
                </span>
              </div>
              <div className="relative">
                <Slider
                  data-testid="crossfader-slider"
                  value={[(crossfaderPosition + 1) * 50]}
                  onValueChange={(value) => {
                    const position = (value[0] / 50) - 1;
                    onCrossfaderChange(position);
                  }}
                  className="w-full min-h-[44px] touch-manipulation"
                  step={1}
                  aria-label={`Crossfader position: ${Math.round((crossfaderPosition + 1) * 50)}%`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round((crossfaderPosition + 1) * 50)}
                />
                <div className="flex justify-between mt-1 text-xs text-sunset-orange/40">
                  <span>100%</span>
                  <span>50/50</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Button - Right side */}
          <div className="flex-shrink-0">
            <Button
              data-testid="next-track-button"
              onClick={onNext}
              size="lg"
              className="bg-gradient-to-r from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6"
            >
              <SkipForward className="w-5 h-5 mr-2" />
              Titre Suivant
            </Button>
          </div>
        </div>

        {/* Master Volume */}
        <div className="flex items-center gap-4">
          <Volume2 className="w-5 h-5 text-purple-400" />
          <Slider
            data-testid="volume-slider"
            value={[volume * 100]}
            onValueChange={(value) => onVolumeChange(value)}
            className="flex-1"
          />
          <span className="text-sm text-sunset-orange/60 w-12 text-right">{Math.round(volume * 100)}%</span>
        </div>
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders (performance optimization)
export default React.memo(DualDeckPlayer);
